import React, { useEffect } from "react";
import { useForm, ValidationError } from "@formspree/react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, Send } from "lucide-react";

const FIELD =
  "w-full bg-transparent border-b-2 border-black/12 focus:border-ink outline-none py-2.5 text-ink text-[15px] placeholder:text-ink/25 transition-colors duration-200";

export default function ContactOverlay({ isOpen, onClose, subject = "" }) {
  const [state, handleSubmit] = useForm("xeenlkqo");

  // Track open
  useEffect(() => {
    if (isOpen) window.posthog?.capture("contact_opened", { source: subject || "general" });
  }, [isOpen, subject]);

  // Track successful submission
  useEffect(() => {
    if (state.succeeded) window.posthog?.capture("contact_submitted", { source: subject || "general" });
  }, [state.succeeded, subject]);

  // Escape to close
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (isOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Lock scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      window.__lenis?.stop();
    } else {
      document.body.style.overflow = "";
      window.__lenis?.start();
    }
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="contact-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[500] flex items-center justify-center p-4 md:p-10"
          style={{ background: "rgba(10,10,10,0.75)", backdropFilter: "blur(10px)" }}
          onClick={onClose}
        >
          <motion.div
            key="contact-card"
            initial={{ opacity: 0, y: 36, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-[880px] rounded-[28px] overflow-hidden border border-black/10 shadow-[0_60px_140px_rgba(0,0,0,0.5)] grid grid-cols-1 md:grid-cols-5"
            onClick={(e) => e.stopPropagation()}
          >
            {/* ── Left panel ── */}
            <div
              className="md:col-span-2 relative flex flex-col justify-between p-8 md:p-12 overflow-hidden"
              style={{ background: "#0D1117" }}
            >
              {/* Subtle glow */}
              <div
                className="absolute top-0 right-0 w-64 h-64 rounded-full pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle, rgba(0,212,170,0.12) 0%, transparent 70%)",
                  transform: "translate(30%, -30%)",
                }}
              />

              <div className="relative z-10">
                <div
                  className="font-mono text-[12px] tracking-[0.28em] uppercase mb-8 font-semibold"
                  style={{ color: "#00D4AA" }}
                >
                  New message
                </div>
                <h2 className="font-serif italic text-[38px] md:text-[50px] leading-[1.04] text-white">
                  Tell me<br />everything.
                </h2>
                <p className="mt-5 text-[13.5px] leading-[1.7]" style={{ color: "rgba(255,255,255,0.4)" }}>
                  I read every message myself. No assistants, no auto-responders. If it's interesting, I'll reply fast.
                </p>
              </div>

              <a
                href="https://linkedin.com/in/samerpik/"
                target="_blank"
                rel="noreferrer"
                className="relative z-10 mt-10 group inline-flex items-center gap-2 transition-opacity hover:opacity-80"
              >
                <div>
                  <div className="font-mono text-[9px] tracking-[0.24em] uppercase mb-1" style={{ color: "rgba(255,255,255,0.2)" }}>
                    LinkedIn
                  </div>
                  <div className="font-mono text-[12px]" style={{ color: "rgba(255,255,255,0.55)" }}>
                    /in/samerpik
                  </div>
                </div>
                <ArrowUpRight size={13} strokeWidth={2} style={{ color: "rgba(255,255,255,0.3)" }} className="mt-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            </div>

            {/* ── Right panel ── */}
            <div className="md:col-span-3 bg-paper p-8 md:p-12 relative">
              {/* Close */}
              <button
                type="button"
                onClick={onClose}
                className="absolute top-5 right-5 w-9 h-9 rounded-full bg-black/6 hover:bg-black/12 transition-colors flex items-center justify-center"
                aria-label="Close"
                data-cursor="magnetic"
                data-cursor-label="CLOSE"
              >
                <X size={15} strokeWidth={2} className="text-ink/60" />
              </button>

              <AnimatePresence mode="wait">
                {state.succeeded ? (
                  /* ── Success state ── */
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="flex flex-col justify-center h-full py-6"
                  >
                    <div className="text-[52px] mb-5 leading-none select-none">✉️</div>
                    <h3 className="font-serif italic text-[36px] md:text-[44px] leading-[1.08] text-ink">
                      Sent. Nice one.
                    </h3>
                    <p className="mt-4 text-mute text-[15px] leading-[1.7] max-w-[34ch]">
                      I'll get back to you properly — not with a template, not with a delay.
                    </p>
                    <button
                      onClick={onClose}
                      className="mt-8 self-start font-mono text-[10px] tracking-[0.22em] uppercase text-ink/40 hover:text-ink transition-colors"
                    >
                      Close ×
                    </button>
                  </motion.div>
                ) : (
                  /* ── Form ── */
                  <motion.form
                    key="form"
                    onSubmit={handleSubmit}
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-7 pt-2"
                    noValidate
                  >
                    {/* Hidden subject — pre-filled from which card triggered */}
                    <input
                      type="hidden"
                      name="subject"
                      value={subject ? `[erpik.com] ${subject}` : "[erpik.com] New enquiry"}
                    />
                    {/* Honeypot — bots fill this, humans don't see it */}
                    <input type="text" name="_gotcha" style={{ display: "none" }} tabIndex={-1} autoComplete="off" />

                    {/* Name */}
                    <div className="space-y-1">
                      <label className="label-mono block">Name</label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="Your name"
                        className={FIELD}
                        autoComplete="name"
                      />
                    </div>

                    {/* Email */}
                    <div className="space-y-1">
                      <label className="label-mono block">Email</label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="you@example.com"
                        className={FIELD}
                        autoComplete="email"
                      />
                      <ValidationError
                        field="email"
                        prefix="Email"
                        errors={state.errors}
                        className="text-red-500 text-[11px] font-mono mt-1 block"
                      />
                    </div>

                    {/* Message */}
                    <div className="space-y-1">
                      <label className="label-mono block">Message</label>
                      <textarea
                        name="message"
                        required
                        rows={4}
                        placeholder="What's on your mind?"
                        className={`${FIELD} resize-none`}
                      />
                      <ValidationError
                        field="message"
                        prefix="Message"
                        errors={state.errors}
                        className="text-red-500 text-[11px] font-mono mt-1 block"
                      />
                    </div>

                    {/* Generic form error */}
                    <ValidationError
                      errors={state.errors}
                      className="text-red-500 text-[11px] font-mono"
                    />

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={state.submitting}
                      data-cursor="magnetic"
                      className="w-full py-4 rounded-full bg-ink text-paper font-mono text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-ink/85 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2.5"
                    >
                      {state.submitting ? (
                        <>
                          <motion.span
                            animate={{ opacity: [1, 0.4, 1] }}
                            transition={{ duration: 1, repeat: Infinity }}
                          >
                            Sending
                          </motion.span>
                          <span>...</span>
                        </>
                      ) : (
                        <>
                          Send it
                          <Send size={13} strokeWidth={2.2} />
                        </>
                      )}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
